import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useForm } from '@tanstack/react-form'
import { Check, TriangleAlert, UserPlus } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { UsersApi, NetworkUser } from '@/services/users/usersApi'
import { RolesApi } from '@/services/users/rolesApi'
import { Team } from '@/services/users/teamsApi'
import { getContext } from '@/services/networks/networksApi'
import { AuthService } from '@/services/core/auth'

interface NewUserModalProps {
  open: boolean
  onClose: () => void
  onCreated: (user: NetworkUser) => void
  /** Teams the user is allowed to assign to */
  allowedTeams: Team[]
}

export function NewUserModal({ open, onClose, onCreated, allowedTeams }: NewUserModalProps) {
  const singleTeam = allowedTeams.length === 1 ? allowedTeams[0] : null
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const hasToken = AuthService.isAuthenticated()
  const [error, setError] = useState('')
  const defaultValues = { name: '', username: '', teamId: singleTeam?.id ?? '' }

  const { data: roles = [] } = useQuery({
    queryKey: ['roles'],
    queryFn: RolesApi.getAll,
    enabled: hasToken && open,
  })

  const sponsorRole = roles.find((r) => r.name === 'Political Sponsor')
  const memberRole = roles.find((r) => r.name === 'Political Network Member Role')

  const form = useForm({
    defaultValues: defaultValues,
    onSubmit: ({ value }) => {
      if (!sponsorRole || !memberRole) {
        setError(t('users.sponsorRoleNotFound'))
        return
      }
      setError('')
      mutation.mutate({
        name: value.name,
        username: value.username,
        teamId: value.teamId,
        roleId: sponsorRole.id,
        memberRoleId: memberRole.id,
        networkId: getContext(),
      })
    },
  })

  const mutation = useMutation({
    mutationFn: UsersApi.create,
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      form.reset(defaultValues)
      onCreated(created)
    },
    onError: (err) => {
      setError((err as Error).message)
    },
  })

  const handleClose = () => {
    form.reset(defaultValues)
    setError('')
    onClose()
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    e.stopPropagation()
    form.handleSubmit()
  }

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) handleClose() }}>
      <DialogContent style={{ maxWidth: 480 }}>
        <DialogHeader>
          <DialogTitle>
            <UserPlus className="inline size-4" style={{ marginRight: 8 }} />
            {t('users.newUserTitle')}
          </DialogTitle>
          <DialogDescription>{t('users.newUserDescription')}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div className="p-field">
            <label>{t('common.name')}</label>
            <form.Field name="name">
              {(field) => (
                <Input
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  placeholder={t('users.namePlaceholder')}
                  required
                  autoFocus
                />
              )}
            </form.Field>
          </div>

          <div className="p-field">
            <label>{t('users.username')}</label>
            <form.Field name="username">
              {(field) => (
                <Input
                  type="email"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  placeholder={t('users.usernamePlaceholder')}
                  required
                />
              )}
            </form.Field>
          </div>

          <div className="p-field">
            <label>{t('common.team')}</label>
            {singleTeam ? (
              <div
                style={{
                  padding: '7px 12px',
                  borderRadius: 6,
                  background: 'var(--ad-neutral-soft)',
                  fontSize: 13,
                  color: 'var(--ad-text-muted)',
                }}
              >
                {singleTeam.name}
              </div>
            ) : (
              <form.Field name="teamId">
                {(field) => (
                  <Select value={field.state.value} onValueChange={(v) => field.handleChange(v ?? '')} required>
                    <SelectTrigger>
                      <SelectValue placeholder={t('users.selectTeam')}>{allowedTeams.find((o) => o.id === field.state.value)?.name ?? null}</SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {allowedTeams.map((team) => (
                        <SelectItem key={team.id} value={team.id}>
                          {team.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </form.Field>
            )}
          </div>

          <div className="p-field">
            <label>{t('users.role')}</label>
            <div
              style={{
                padding: '7px 12px',
                borderRadius: 6,
                background: 'var(--ad-neutral-soft)',
                fontSize: 13,
                color: 'var(--ad-text-muted)',
              }}
            >
              {t('users.rolePoliticalSponsor')}
            </div>
          </div>

          {error && (
            <div style={{ color: 'var(--ad-danger)', fontSize: 13 }}>
              <TriangleAlert className="inline size-3.5" style={{ marginRight: 6 }} />
              {error}
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" size="sm" onClick={handleClose} disabled={mutation.isPending}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" size="sm" loading={mutation.isPending} onClick={handleSubmit}>
              <Check className="size-4" />
              {t('users.createUser')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
